import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, Plus, RefreshCw, Search, Trash2, Edit, BarChart3,
  Upload, List, Target, Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SegmentsPage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('live');
  const [segments, setSegments] = useState([]);
  const [staticLists, setStaticLists] = useState([]);
  const [rfm, setRfm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [newListOpen, setNewListOpen] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [newListDesc, setNewListDesc] = useState('');

  const token = localStorage.getItem('token');
  const authHdr = { Authorization: `Bearer ${token}` };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [segRes, listRes, rfmRes] = await Promise.all([
        axios.get(`${API}/segments`, { headers: authHdr }),
        axios.get(`${API}/segments/static-lists/all`, { headers: authHdr }),
        axios.get(`${API}/segments/rfm/overview`, { headers: authHdr }),
      ]);
      setSegments(segRes.data.segments || []);
      setStaticLists(listRes.data.lists || []);
      setRfm(rfmRes.data);
    } catch (e) {
      console.error('[Segments] load', e);
      toast.error('Failed to load segments');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const refreshSegment = async (id) => {
    try {
      const r = await axios.post(`${API}/segments/${id}/evaluate`, {}, { headers: authHdr });
      toast.success(`${r.data.count.toLocaleString()} members`);
      fetchAll();
    } catch (e) {
      toast.error('Refresh failed');
    }
  };

  const deleteSegment = async (id, name) => {
    if (!window.confirm(`Delete segment "${name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/segments/${id}`, { headers: authHdr });
      toast.success('Segment deleted');
      fetchAll();
    } catch (e) { toast.error('Delete failed'); }
  };

  const deleteList = async (id, name) => {
    if (!window.confirm(`Delete list "${name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/segments/static-lists/${id}`, { headers: authHdr });
      toast.success('List deleted');
      fetchAll();
    } catch (e) { toast.error('Delete failed'); }
  };

  const createStaticList = async () => {
    if (!newListName.trim()) { toast.error('Name required'); return; }
    try {
      const r = await axios.post(`${API}/segments/static-lists`, { name: newListName, description: newListDesc }, { headers: authHdr });
      toast.success('List created');
      setNewListOpen(false);
      setNewListName('');
      setNewListDesc('');
      fetchAll();
      navigate(`/customers/segments/static-lists/${r.data.list.id}`);
    } catch (e) { toast.error('Create failed'); }
  };

  const convertRfmToList = async (tierId, tierName) => {
    if (!window.confirm(`Create a snapshot list of "${tierName}" members right now?`)) return;
    try {
      const r = await axios.post(`${API}/segments/rfm/${tierId}/convert-to-live`, {}, { headers: authHdr });
      toast.success(`Created list with ${r.data.list.member_count} members`);
      fetchAll();
      setTab('lists');
    } catch (e) { toast.error('Convert failed'); }
  };

  const filteredSegs = segments.filter(s => !search || (s.name || '').toLowerCase().includes(search.toLowerCase()));
  const filteredLists = staticLists.filter(s => !search || (s.name || '').toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="segments-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-purple-500/30 to-indigo-500/30 flex items-center justify-center">
              <Target className="w-5 h-5 text-purple-300" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-semibold text-white">Customer Segments</h1>
              <p className="text-sm text-gray-400">Build audiences for bulk Email, WhatsApp, SMS and Journeys.</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <Input
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 w-56 bg-wt-bg border-wt-border"
              data-testid="segments-search"
            />
          </div>
          <Button variant="outline" size="sm" onClick={fetchAll} data-testid="segments-refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          {tab === 'live' && (
            <Button onClick={() => navigate('/customers/segments/builder/new')} className="bg-gradient-to-r from-purple-500 to-indigo-500 hover:opacity-90" data-testid="create-segment-btn">
              <Plus className="w-4 h-4 mr-1" /> Create Segment
            </Button>
          )}
          {tab === 'lists' && (
            <Button onClick={() => setNewListOpen(true)} className="bg-gradient-to-r from-purple-500 to-indigo-500 hover:opacity-90" data-testid="create-list-btn">
              <Plus className="w-4 h-4 mr-1" /> Create List
            </Button>
          )}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="bg-wt-surface border border-wt-border">
          <TabsTrigger value="live" className="data-[state=active]:bg-purple-500/20" data-testid="tab-live">
            <Zap className="w-4 h-4 mr-1" /> Live Segments
          </TabsTrigger>
          <TabsTrigger value="lists" className="data-[state=active]:bg-purple-500/20" data-testid="tab-lists">
            <List className="w-4 h-4 mr-1" /> Static Lists
          </TabsTrigger>
          <TabsTrigger value="rfm" className="data-[state=active]:bg-purple-500/20" data-testid="tab-rfm">
            <BarChart3 className="w-4 h-4 mr-1" /> RFM Segments
          </TabsTrigger>
        </TabsList>

        {/* LIVE SEGMENTS */}
        <TabsContent value="live" className="mt-4">
          <div className="bg-wt-surface border border-wt-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-wt-paper border-b border-wt-border">
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-400">
                    <th className="px-4 py-3">Segment Name</th>
                    <th className="px-4 py-3">Segment Size</th>
                    <th className="px-4 py-3">Last Evaluated</th>
                    <th className="px-4 py-3 w-32 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSegs.length === 0 && (
                    <tr><td colSpan="4" className="px-4 py-10 text-center text-gray-500">
                      {search ? 'No segments match your search.' : 'No segments yet. Click "Create Segment" to build your first audience.'}
                    </td></tr>
                  )}
                  {filteredSegs.map(s => (
                    <tr key={s.id} className="border-b border-wt-border hover:bg-wt-paper/50 transition" data-testid={`segment-row-${s.id}`}>
                      <td className="px-4 py-3">
                        <button onClick={() => navigate(`/customers/segments/builder/${s.id}`)} className="text-left text-white hover:text-purple-300 font-medium">
                          {s.name}
                        </button>
                        {s.description && <div className="text-xs text-gray-500 mt-0.5">{s.description}</div>}
                      </td>
                      <td className="px-4 py-3 text-white">{(s.member_count ?? '—').toLocaleString?.() ?? s.member_count ?? '—'}</td>
                      <td className="px-4 py-3 text-gray-400">{fmtDate(s.last_evaluated_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <Button size="sm" variant="ghost" onClick={() => refreshSegment(s.id)} title="Refresh count" data-testid={`refresh-seg-${s.id}`}>
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => navigate(`/customers/segments/builder/${s.id}`)} title="Edit">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteSegment(s.id, s.name)} title="Delete" data-testid={`delete-seg-${s.id}`}>
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* STATIC LISTS */}
        <TabsContent value="lists" className="mt-4">
          <div className="bg-wt-surface border border-wt-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-wt-paper border-b border-wt-border">
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-400">
                    <th className="px-4 py-3">List Name</th>
                    <th className="px-4 py-3">Members</th>
                    <th className="px-4 py-3">Updated</th>
                    <th className="px-4 py-3 w-24 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLists.length === 0 && (
                    <tr><td colSpan="4" className="px-4 py-10 text-center text-gray-500">
                      {search ? 'No lists match your search.' : 'No lists yet. Create a static list and upload a CSV of customers.'}
                    </td></tr>
                  )}
                  {filteredLists.map(l => (
                    <tr key={l.id} className="border-b border-wt-border hover:bg-wt-paper/50 transition" data-testid={`list-row-${l.id}`}>
                      <td className="px-4 py-3">
                        <button onClick={() => navigate(`/customers/segments/static-lists/${l.id}`)} className="text-left text-white hover:text-purple-300 font-medium">
                          {l.name}
                        </button>
                        {l.rfm_tier && <Badge variant="outline" className="ml-2 text-xs">RFM · {l.rfm_tier}</Badge>}
                        {l.description && <div className="text-xs text-gray-500 mt-0.5">{l.description}</div>}
                      </td>
                      <td className="px-4 py-3 text-white">{(l.member_count ?? 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-gray-400">{fmtDate(l.updated_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <Button size="sm" variant="ghost" onClick={() => navigate(`/customers/segments/static-lists/${l.id}`)} title="Open">
                          <Upload className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteList(l.id, l.name)} data-testid={`delete-list-${l.id}`}>
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* RFM SEGMENTS */}
        <TabsContent value="rfm" className="mt-4">
          {rfm ? (
            <>
              <div className="mb-4 p-4 bg-wt-paper border border-wt-border rounded-lg">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div className="text-sm text-gray-400">
                    Auto-computed from customer order history.<br />
                    <span className="text-gray-300">Company averages:</span> {Number(rfm.avg_orders || 0).toFixed(1)} orders/customer · ${Number(rfm.avg_spend || 0).toFixed(0)} avg spend
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchAll}><RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Recompute</Button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {(rfm.tiers || []).map(t => (
                  <div key={t.id} className="p-4 bg-wt-surface border border-wt-border rounded-lg hover:border-purple-500/50 transition" data-testid={`rfm-tier-${t.id}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="text-white font-semibold text-lg">{t.name}</div>
                        <div className="text-2xl font-bold text-purple-300 mt-1">{(t.count || 0).toLocaleString()}</div>
                        <div className="text-xs text-gray-400 mt-1">customers</div>
                      </div>
                    </div>
                    <div className="text-xs text-gray-400 mt-3 min-h-[32px]">{t.description}</div>
                    <Button size="sm" className="mt-3 w-full" variant="outline" disabled={!t.count} onClick={() => convertRfmToList(t.id, t.name)} data-testid={`rfm-convert-${t.id}`}>
                      <Plus className="w-3 h-3 mr-1" /> Save as Static List
                    </Button>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="p-10 text-center text-gray-500">Computing RFM tiers…</div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create static list dialog */}
      <Dialog open={newListOpen} onOpenChange={setNewListOpen}>
        <DialogContent className="bg-wt-surface border-wt-border">
          <DialogHeader>
            <DialogTitle className="text-white">Create Static List</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-white">Name</Label>
              <Input value={newListName} onChange={(e) => setNewListName(e.target.value)} placeholder="e.g., VIP Customers Q2" className="bg-wt-bg mt-1" data-testid="new-list-name" />
            </div>
            <div>
              <Label className="text-white">Description (optional)</Label>
              <Input value={newListDesc} onChange={(e) => setNewListDesc(e.target.value)} className="bg-wt-bg mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setNewListOpen(false)}>Cancel</Button>
            <Button onClick={createStaticList} data-testid="submit-new-list">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SegmentsPage;
